/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import appleWalletBadge from "../../../../../../packages/business/assets/images/partners/apple-wallet.svg";
import googleWalletBadge from "../../../../../../packages/business/assets/images/partners/google-wallet.svg";

/**
 * Add-to-wallet badges, shown under a card and again beneath its QR.
 *
 * Both badges are always rendered. A badge stays visibly disabled until its
 * install URL is available, so a failed pass lookup cannot make the wallet
 * actions disappear. Keeping them outside the card's transformed stack also
 * prevents the stack's clipping/overlay layers from intercepting their clicks.
 */
export type WalletBadgesProps = {
  /** Shorter badges, for the row under a full-bleed pass on a phone. */
  compact?: boolean;
  /** Taller badges, stacked and centred, for the add-to-wallet page. */
  large?: boolean;
  appleUrl?: string | null;
  googleUrl?: string | null;
};

export const WalletBadges = defineComponent<WalletBadgesProps>({
  name: "WalletBadges",
  props: ["compact", "large", "appleUrl", "googleUrl"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const badges = [
        {
          src: appleWalletBadge,
          alt: t("customer.gift_card.add_to_apple_wallet"),
          href: props.appleUrl,
        },
        {
          src: googleWalletBadge,
          alt: t("customer.gift_card.add_to_google_wallet"),
          href: props.googleUrl,
        },
      ];

      const height = props.large ? "h-12" : props.compact ? "h-7" : "h-9";

      return (
        <div
          class={
            props.large
              ? "relative z-30 flex flex-col items-center gap-3"
              : "relative z-30 flex items-center gap-2"
          }
        >
          {badges.map((badge) =>
            badge.href ? (
              <a
                key={badge.alt}
                href={badge.href!}
                aria-label={badge.alt}
                class={[
                  "relative z-20 inline-flex shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  // The official artwork carries its own rounded shape; the
                  // dark backing only helps the small badges on light cards.
                  props.large ? "" : "bg-base-content shadow-sm",
                ]}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                <img
                  src={badge.src}
                  alt={badge.alt}
                  class={`${height} block`}
                />
              </a>
            ) : (
              <button
                key={badge.alt}
                type="button"
                disabled
                aria-label={badge.alt}
                aria-disabled="true"
                title={badge.alt}
                class={[
                  "inline-flex shrink-0 cursor-not-allowed rounded-lg opacity-40 grayscale",
                  props.large ? "" : "bg-base-content",
                ]}
              >
                <img
                  src={badge.src}
                  alt=""
                  aria-hidden="true"
                  class={`block ${height}`}
                />
              </button>
            ),
          )}
        </div>
      );
    };
  },
});
